// ============================================================
//  src/modal-drive.js — Modales para la integración con Drive
// ============================================================

import { mostrarToast, mostrarConfirmacion, cerrarToast,
         toggleBoton }                                     from './ui.js';
import { requiereReautorizacionDrive }                     from './drive.js';
import { mensajeErrorDrive }                               from './drive-errors.js';
import { guardarArchivoEnEdicion, obtenerArchivosEdicion,
         eliminarArchivoDeEdicion }                        from './firestore.js';
import { yaExisteModeracion, crearEventoModeracion }       from './moderacion.js';
import { mostrarModalAutorizacion }                        from './modal-autorizar-drive.js';
import { t }                                               from './i18n.js';
import { getAuth }                                         from 'firebase/auth';
import { safeUrl }                                         from './security.js';
import { getProvider }                                     from './storage/storage.js';
import { getPerfilActual }                                 from './perfil.js';

let contextoArchivos = null;

// ============================================================
//  Modal archivos
// ============================================================

export function abrirModalArchivos(edicion, tipo) {
    _mostrarModalArchivos(edicion, tipo);
}

async function _mostrarModalArchivos(edicion, tipo) {

    contextoArchivos = { edicion, tipo };

    const etiqueta = {
        nombre: t(`tipo.${tipo}.nombre`),
        icono:  t(`tipo.${tipo}.icono`)
    };

    const elTitulo = document.getElementById('modal-archivos-titulo');

    if (elTitulo) {
        elTitulo.textContent =
            `${etiqueta.icono} ${etiqueta.nombre} — ${edicion.concurso} ${edicion.año}`;
    }

    const elDesc = document.getElementById('archivos-tipo-desc');
    if (elDesc) {
        elDesc.textContent = t(`tipo.${tipo}.desc`);
    }

    const overlay = document.getElementById('modal-archivos');
    overlay?.classList.add('modal--visible');

    renderizarListaArchivos([]);
    mostrarSpinnerArchivos(true);

    try {

        const archivos = await obtenerArchivosEdicion(edicion.id, tipo);

        renderizarListaArchivos(archivos);

    } catch (error) {

        console.error('[ModalDrive] Error al cargar archivos:', error);

        mostrarToast(
            mensajeErrorDrive(error, t('toast.drive.error.cargar2')),
            'error',
            7000
        );

        if (requiereReautorizacionDrive(error)) {
            mostrarModalAutorizacion(() => _mostrarModalArchivos(edicion, tipo));
        }

    } finally {

        mostrarSpinnerArchivos(false);

    }
}

export function cerrarModalArchivos() {

    const overlay = document.getElementById('modal-archivos');
    overlay?.classList.remove('modal--visible');
    contextoArchivos = null;

}

// ============================================================
//  Spinner
// ============================================================

function mostrarSpinnerArchivos(mostrar) {

    const spinner = document.getElementById('archivos-spinner');

    if (spinner) {
        spinner.style.display = mostrar ? 'flex' : 'none';
    }

}

// ============================================================
//  Render lista segura
// ============================================================

function renderizarListaArchivos(archivos) {

    const lista = document.getElementById('archivos-lista');

    if (!lista) return;

    lista.replaceChildren();

    if (archivos.length === 0) {

        const vacio = document.createElement('div');
        vacio.className = 'archivos-vacio';

        const texto = document.createElement('p');
        texto.textContent = t('archivos.vacio');

        vacio.appendChild(texto);
        lista.appendChild(vacio);

        return;

    }

    archivos.forEach(archivo => {

        const item = document.createElement('div');
        item.className = 'archivo-item';
        item.dataset.archivoId   = String(archivo.id || '');
        item.dataset.fileId      = String(archivo.fileId || archivo.driveId || '');
        item.dataset.storageType = String(archivo.storageType || 'user_drive');

        const icono = document.createElement('div');
        icono.className = 'archivo-item__icono';
        icono.textContent = obtenerIconoMime(archivo.mimeType);

        const info = document.createElement('div');
        info.className = 'archivo-item__info';

        const nombre = document.createElement('span');
        nombre.className = 'archivo-item__nombre';
        nombre.textContent = archivo.nombre;

        const meta = document.createElement('span');
        meta.className = 'archivo-item__meta';
        meta.textContent = formatearTamano(archivo.tamaño ?? 0);

        info.append(nombre, meta);

        const acciones = document.createElement('div');
        acciones.className = 'archivo-item__acciones';

        const esImagen = archivo.mimeType?.startsWith('image/');
        const esPdf = archivo.mimeType === 'application/pdf';
        const driveUrlSegura = safeUrl(archivo.fileUrl ?? archivo.driveUrl);

        if ((esImagen || esPdf) && driveUrlSegura) {

            const preview = document.createElement('a');
            preview.href = driveUrlSegura;
            preview.target = '_blank';
            preview.rel = 'noopener noreferrer';
            preview.className = 'btn-preview';
            preview.title = t('archivos.btn.preview');
            preview.dataset.i18nAria = 'archivos.btn.preview';
            preview.setAttribute('aria-label', t('archivos.btn.preview'));
            preview.textContent = '👁️';

            acciones.appendChild(preview);

        }

        if (driveUrlSegura) {
            const abrir = document.createElement('a');
            abrir.href = driveUrlSegura;
            abrir.target = '_blank';
            abrir.rel = 'noopener noreferrer';
            abrir.className = 'btn-descargar';
            abrir.title = t('archivos.btn.abrir');
            abrir.dataset.i18nAria = 'archivos.btn.abrir';
            abrir.setAttribute('aria-label', t('archivos.btn.abrir'));
            abrir.textContent = '↗';

            acciones.appendChild(abrir);
        }

        const eliminar = document.createElement('button');
        eliminar.className = 'btn-eliminar-archivo';
        eliminar.type = 'button';
        eliminar.title = t('archivos.btn.eliminar-archivo');
        eliminar.dataset.i18nAria = 'archivos.btn.eliminar-archivo';
        eliminar.setAttribute('aria-label', t('archivos.btn.eliminar-archivo'));
        eliminar.textContent = '🗑️';

        eliminar.addEventListener('click', () => {
            manejarEliminarArchivo(
                archivo.id,
                archivo.fileId ?? archivo.driveId,
                archivo.storageType ?? 'user_drive',
                archivo.providerMeta ?? null
            );
        });

        acciones.appendChild(eliminar);

        item.append(icono, info, acciones);

        lista.appendChild(item);

    });

}

// ============================================================
//  Eliminar archivo
// ============================================================

async function manejarEliminarArchivo(archivoId, fileId, storageType = 'user_drive', providerMeta = null) {

    if (!contextoArchivos) return;

    const { edicion, tipo } = contextoArchivos;

    const confirmar = await mostrarConfirmacion(
        t('archivos.confirmar.titulo'),
        t('archivos.confirmar.subtitulo')
    );

    if (!confirmar) return;

    mostrarSpinnerArchivos(true);

    try {

        if (fileId) {
            await getProvider(storageType).delete(fileId, providerMeta);
        }

        await eliminarArchivoDeEdicion(edicion.id, archivoId);

        mostrarToast(t('archivos.toast.eliminado'), 'exito');

        const archivosActualizados =
            await obtenerArchivosEdicion(edicion.id, tipo);

        renderizarListaArchivos(archivosActualizados);

    } catch (error) {

        console.error('[ModalDrive] Error al eliminar archivo:', error);

        mostrarToast(
            mensajeErrorDrive(error, t('toast.drive.error.eliminar')),
            'error',
            7000
        );

        if (requiereReautorizacionDrive(error)) {
            mostrarModalAutorizacion(() => _mostrarModalArchivos(edicion, tipo));
        }

    } finally {

        mostrarSpinnerArchivos(false);

    }
}

// ============================================================
//  Subir archivo
// ============================================================

export async function manejarSeleccionArchivo(evento) {

    const archivo = evento.target.files?.[0];

    if (!archivo || !contextoArchivos) return;

    const { edicion, tipo } = contextoArchivos;

    // Bloquear uploads mientras la solicitud de Contest Manager esté pendiente de aprobación
    if (getPerfilActual()?.managedDrivePending === true) {
        mostrarToast('Subida de archivos bloqueada — la solicitud de Contest Manager está pendiente de aprobación.', 'error', 5000);
        evento.target.value = '';
        return;
    }

    const storageType = getPerfilActual()?.storagePreference ?? 'user_drive';

    let provider;
    try {
        provider = getProvider(storageType);
    } catch {
        mostrarToast(t('toast.storage.error') || 'Storage no disponible', 'error');
        evento.target.value = '';
        return;
    }

    // Verificar autorización para el provider seleccionado
    if (!provider.isAuthorized()) {
        if (storageType === 'user_drive') {
            mostrarToast(t('toast.drive.auth.requerida'), 'error', 6000);
            mostrarModalAutorizacion(() => {});
        } else {
            mostrarToast(t('toast.drive.sesion-requerida'), 'error', 5000);
        }
        evento.target.value = '';
        return;
    }

    const toastCargandoId = mostrarToast(t('toast.drive.preparando'), 'cargando', 0);
    toggleBoton('btn-subir-archivo', false, t('drive.subiendo'));
    mostrarSpinnerArchivos(true);

    try {

        const resultado = await provider.upload(archivo, { edicion, tipo });

        const archivoDocId = await guardarArchivoEnEdicion(edicion.id, {
            storageType,
            fileId:       resultado.fileId,
            fileUrl:      resultado.fileUrl ?? null,
            providerMeta: resultado.providerMeta ?? null,
            tipo,
            nombre:       archivo.name,
            mimeType:     archivo.type,
            tamaño:       archivo.size,
        });

        mostrarToast(
            t('toast.drive.subido', { nombre: archivo.name }),
            'exito'
        );

        // Crear moderación global si no existe activa (pendiente/aprobada) para tipo+concurso+año+modo
        if ((tipo === 'res' || tipo === 'cert') && archivoDocId) {
            const yaExiste = await yaExisteModeracion(tipo, edicion.concurso, edicion.año, edicion.modo).catch(() => true);
            if (!yaExiste) {
                crearEventoModeracion({
                    edicionId: edicion.id,
                    archivoId: archivoDocId,
                    tipo,
                    concurso:  edicion.concurso,
                    año:       edicion.año,
                    modo:      edicion.modo,
                    subidoPor: getAuth().currentUser?.uid ?? '',
                }).catch(e => console.warn('[ModalDrive] Error moderación:', e));
            }
        }

        const archivosActualizados =
            await obtenerArchivosEdicion(edicion.id, tipo);

        renderizarListaArchivos(archivosActualizados);

    } catch (error) {

        console.error('[ModalDrive] Error al subir archivo:', error);

        mostrarToast(
            mensajeErrorDrive(error, t('toast.drive.error.subir', { error: error?.message ?? '' })),
            'error',
            7000
        );

        if (requiereReautorizacionDrive(error)) {
            mostrarModalAutorizacion(() => _mostrarModalArchivos(edicion, tipo));
        }

    } finally {

        cerrarToast(toastCargandoId);
        mostrarSpinnerArchivos(false);
        toggleBoton('btn-subir-archivo', true);
        evento.target.value = '';

    }
}

// ============================================================
//  Helpers
// ============================================================

function obtenerIconoMime(mimeType = '') {

    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('zip')) return '🗜️';
    if (mimeType.includes('text')) return '📝';
    if (mimeType === 'application/vnd.ms-excel'
        || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return '📊';

    return '📎';

}

function formatearTamano(bytes) {

    if (bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;

    return `${(bytes / 1024 ** 2).toFixed(1)} MB`;

}
